import React from 'react';
import MapLibreGL from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { geojsonFiles } from '../config/geojsonFiles';

interface MapProps {
  style?: React.CSSProperties;
}

const Map: React.FC<MapProps> = ({ style }) => {
  const mapContainer = React.useRef<HTMLDivElement>(null);
  const map = React.useRef<MapLibreGL.Map | null>(null);

  React.useEffect(() => {
    if (map.current) return; // 이미 지도가 초기화되어 있으면 리턴

    if (mapContainer.current) {
      map.current = new MapLibreGL.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            'osm': {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors'
            }
          },
          layers: [
            {
              id: 'osm-tiles',
              type: 'raster',
              source: 'osm',
              minzoom: 0,
              maxzoom: 22
            }
          ]
        },
        center: [127.5, 34.2], // 여수 중심 좌표로 변경
        zoom: 8  // 줌 레벨을 낮춰서 더 넓은 영역을 보이게
      });

      // 지도가 로드된 후 모든 GeoJSON 데이터 추가
      map.current.on('load', async () => {
        try {
          console.log('지도 로드 완료, GeoJSON 파일들 로드 시작...');
          
          const allBounds: MapLibreGL.LngLatBounds[] = [];
          
          // 모든 GeoJSON 파일을 순차적으로 로드
          for (let i = 0; i < geojsonFiles.length; i++) {
            const config = geojsonFiles[i];
            if (!config.visible) continue;
            
            try {
              console.log(`${config.name} 로드 중...`);
              
              // GeoJSON 파일 로드
              const response = await fetch(`/data/${config.filename}`);
              if (!response.ok) {
                console.error(`${config.filename} 로드 실패: HTTP ${response.status}`);
                continue;
              }
              
              const geojsonData = await response.json();
              console.log(`${config.name} 데이터 로드 완료:`, geojsonData);
              
              const sourceId = `geojson-${i}`;
              
              // GeoJSON 소스 추가
              map.current?.addSource(sourceId, {
                type: 'geojson',
                data: geojsonData
              });
              
              // 경계 계산
              if (geojsonData.features && geojsonData.features.length > 0) {
                const feature = geojsonData.features[0];
                if (feature.geometry && feature.geometry.coordinates) {
                  const coordinates = feature.geometry.coordinates[0];
                  const bounds = coordinates.reduce((bounds: any, coord: number[]) => {
                    return bounds.extend(coord);
                  }, new MapLibreGL.LngLatBounds(coordinates[0], coordinates[0]));
                  
                  allBounds.push(bounds);
                  console.log(`${config.name} 경계:`, bounds);
                }
              }
              
              // 채우기 레이어 추가
              map.current?.addLayer({
                id: `${sourceId}-fill`,
                type: 'fill',
                source: sourceId,
                paint: {
                  'fill-color': config.color,
                  'fill-opacity': config.opacity,
                  'fill-outline-color': config.color
                }
              });
              
              // 경계선 레이어 추가
              map.current?.addLayer({
                id: `${sourceId}-border`,
                type: 'line',
                source: sourceId,
                paint: {
                  'line-color': config.color,
                  'line-width': 3,
                  'line-opacity': 1.0
                }
              });
              
              // 텍스트 레이어 추가 (VTS Center 속성이 있는 경우)
              map.current?.addLayer({
                id: `${sourceId}-symbol`,
                type: 'symbol',
                source: sourceId,
                layout: {
                  'text-field': ['get', 'VTS Center'],
                  'text-font': ['Open Sans Bold'],
                  'text-size': 14,
                  'text-offset': [0, 0],
                  'text-anchor': 'center'
                },
                paint: {
                  'text-color': config.color,
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2
                }
              });
              
              // 클릭 이벤트 추가
              map.current?.on('click', `${sourceId}-fill`, (e) => {
                if (e.features && e.features[0]) {
                  const feature = e.features[0];
                  const properties = feature.properties;
                  
                  // 팝업 생성
                  // @ts-ignore
                  const popup = new MapLibreGL.Popup()
                    .setLngLat(e.lngLat)
                    .setHTML(`
                      <div style="padding: 10px;">
                        <h3 style="margin: 0 0 10px 0; color: ${config.color};">${config.name}</h3>
                        ${properties['VTS Center'] ? `<p><strong>VTS Center:</strong> ${properties['VTS Center']}</p>` : ''}
                        ${properties['호출명칭'] ? `<p><strong>호출명칭:</strong> ${properties['호출명칭']}</p>` : ''}
                        ${properties['통신방법'] ? `<p><strong>통신방법:</strong> ${properties['통신방법']}</p>` : ''}
                        ${properties['CH_관제통신용 채널'] ? `<p><strong>관제통신용 채널:</strong> ${properties['CH_관제통신용 채널']}</p>` : ''}
                        ${properties['CH_호출 및 비상'] ? `<p><strong>호출 및 비상:</strong> ${properties['CH_호출 및 비상']}</p>` : ''}
                        ${properties['비고'] ? `<p><strong>비고:</strong> ${properties['비고']}</p>` : ''}
                      </div>
                    `)
                    .addTo(map.current!);
                }
              });
              
              // 마우스 커서 변경
              map.current?.on('mouseenter', `${sourceId}-fill`, () => {
                map.current!.getCanvas().style.cursor = 'pointer';
              });
              
              map.current?.on('mouseleave', `${sourceId}-fill`, () => {
                map.current!.getCanvas().style.cursor = '';
              });
              
              console.log(`${config.name} 레이어가 성공적으로 추가되었습니다.`);
              
            } catch (error) {
              console.error(`${config.name} 로드 실패:`, error);
            }
          }
          
          // 모든 경계를 포함하는 뷰포트로 조정
          if (allBounds.length > 0) {
            const combinedBounds = allBounds.reduce((combined, bounds) => {
              return combined.extend(bounds.getNorthWest()).extend(bounds.getSouthEast());
            });
            
            console.log('전체 GeoJSON 경계:', combinedBounds);
            
            map.current?.fitBounds(combinedBounds, {
              padding: 50,
              maxZoom: 12
            });
          }
          
          console.log('모든 GeoJSON 파일 로드 완료!');
          
        } catch (error) {
          console.error('GeoJSON 로드 중 오류 발생:', error);
        }
      });
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={mapContainer}
      style={{
        width: '100%',
        height: '100%',
        ...style
      }}
    />
  );
};

export default Map;
